/**
 * Build-time mirror of order-service's `OrderTypeEnum`
 * (order-service/src/main/java/com/escreenlogic/api/enums/OrderTypeEnum.java),
 * kept as an offline fallback for the Product Type label so a human-readable
 * title shows even before the synced Order Type Catalog has picked up an
 * entry. Covers only active order types — entries the enum marks `@Deprecated`
 * or lists in `LEGACY_PRODUCTS` are omitted since they can't appear as an
 * active service type. The synced Order Type Catalog remains authoritative —
 * this map is only consulted when the catalog has no match, and any order
 * type not found here falls back to the raw enum name. Update this file if
 * the order-service enum titles change.
 *
 * <b>Why this lives in `shared/`.</b> Two surfaces decode these enums now, and
 * they sit on opposite sides of the app: the internal Manage Service Types
 * screen, and the vendor-facing Available Work feed
 * (`availableWorkReportType`). The vendor surface deliberately does NOT reach
 * for the synced Order Type Catalog the internal screen prefers — that catalog
 * is fetched over an internal-scoped endpoint a vendor holds no grant for, so
 * on that surface this map is not a fallback but the only decoder.
 *
 * A handful of enums map to the SAME title on purpose (`APPRAISAL` and
 * `LRES_APPRAISAL` are both 'Commercial Appraisal'), mirroring the order-service
 * enum. Any filter seeded from decoded values therefore collapses those into
 * one option that matches both — correct for a human-facing filter, but worth
 * knowing before treating a decoded title as a unique key.
 */
export const ORDER_TYPE_TITLES: Record<string, string> = {
  APPRAISAL: 'Commercial Appraisal',
  LRES_APPRAISAL: 'Commercial Appraisal',
  ASBESTOS_ABATEMENT: 'Asbestos Abatement',
  ASBESTOS_INSPECTION: 'Asbestos Inspection',
  BK_CRE_AVM: 'ICE CAVM',
  CLIMATE_CHECK: 'ClimateCheck Risk Report',
  DRIVEBY_CSIE_TIER_1_DAYS_7: 'Commercial Eval Sales & Income, Drive By (Tier 1)',
  DRIVEBY_CSIE_TIER_2_DAYS_7: 'Commercial Eval Sales & Income, Drive By (Tier 2)',
  INTERIOR_CSIE_TIER_1_DAYS_10: 'Commercial Eval Sales & Income, Interior (Tier 1)',
  INTERIOR_CSIE_TIER_2_DAYS_10: 'Commercial Eval Sales & Income, Interior (Tier 2)',
  DRIVEBY_CSOE_TIER_1_DAYS_7: 'Commercial Eval Sales Only, Drive By (Tier 1)',
  DRIVEBY_CSOE_TIER_2_DAYS_7: 'Commercial Eval Sales Only, Drive By (Tier 2)',
  INTERIOR_CSOE_TIER_1_DAYS_7: 'Commercial Eval Sales Only, Interior (Tier 1)',
  INTERIOR_CSOE_TIER_2_DAYS_7: 'Commercial Eval Sales Only, Interior (Tier 2)',

  DRIVEBY_RES_EVAL_TIER1: 'Residential Eval, Drive-By',
  INTERIOR_RES_EVAL_TIER1: 'Residential Eval, Interior',

  DRIVEBY_CSIB_DAYS_7: 'Commercial BPO, Sales & Income, Drive-By',
  INTERIOR_CSIB_DAYS_7: 'Commercial BPO, Sales & Income, Interior',
  DRIVEBY_CSOB_DAYS_7: 'Commercial BPO, Sales Only, Drive-By',
  INTERIOR_CSOB_DAYS_7: 'Commercial BPO, Sales Only, Interior',

  RESIDENTIAL_BPO_DRIVE_BY: 'Residential BPO, Drive-By',
  RESIDENTIAL_BPO_INTERIOR: 'Residential BPO, Interior',

  EDR_REVIEW: 'EDR Review',
  DEMOLITION: 'Demolition',
  ENHANCED_FILE_REVIEW: 'Enhanced File Review',
  ENVIRONMENTAL_DRILLING: 'Environmental Drilling',
  ENVIROSCREEN_FLASH: 'EnviroPreScreen',
  ENVIROSCREEN_SUBJECT_ONLY: 'EnviroPreScreen Subject Only',
  ENVIROSCREEN_SUBJECT_SPECIAL: 'EnviroPreScreen Subject Only - Special',
  ENVIROSCREEN_DETAIL: 'EnviroPreScreen Advanced',
  ENVIROSCREEN_EXPERT: 'EnviroPreScreen Pro Insight',
  ENVIROSCREEN_SUBJECT_PRO_INSIGHT: 'EnviroPreScreen Subject Pro Insight',
  ENVIROSCREEN_LTE: 'EnviroScreen LTE',
  ENVIROSCREEN_CONVENTIONAL: 'RSRA',
  ENVIROSCREEN_SBA: 'RSRA-SBA',
  ENVIROSCREEN_PRO: 'EnviroScreen Pro',
  ENVIRONMENTAL_TRANSACTION_SCREEN: 'Transaction Screen Assessment',
  FIRST_AMERICAN_CPSI: 'Commercial Property Site Inspection',
  FIRST_AMERICAN_MVR: 'Mean Value Report',
  FIRST_AMERICAN_RCR: 'Replacement Cost Report',
  FIRST_AMERICAN_LCER: 'Land Commercial Evaluation Report',
  FIRST_AMERICAN_RAR: 'Restricted Appraisal Report',
  FIRST_AMERICAN_CER: 'Commercial Evaluation Report',
  FIRST_AMERICAN_CER_PLUS_INCOME: 'Commercial Evaluation plus Income Report',
  FIRST_AMERICAN_CER_PLUS_INTERIOR_INSPECTION: 'Commercial Evaluation plus Interior Inspection Report',
  FIRST_AMERICAN_CER_INCOME_PLUS_INTERIOR_INSPECTION: 'Commercial Evaluation plus Income and Interior Inspection Report',
  GEO_TECHNICAL_DRILLING: 'Geo-Technical Drilling',
  HAND_AUGER: 'Hand Auger',
  HAZARDOUS_WASTE_TANK_ASSESSMENT: 'Hazardous Waste Tank Assessment',

  LERETA_COMMERCIAL_FLOOD_ONCE: 'Commercial Lereta Flood - Basic Certificate',
  LERETA_COMMERCIAL_FLOOD_LIFE_OF_LOAN: 'Commercial Lereta Flood - Life of Loan',
  LERETA_RES_FLOOD_ONCE: 'Residential Lereta Flood - Basic Certificate',
  LERETA_RES_FLOOD_LIFE_OF_LOAN: 'Residential Lereta Flood - Life of Loan',
  LPST_PACKAGE: 'LPST Package',
  OPERATIONS_AND_MAINTENANCE: 'Operations and Maintenance',
  OTHER_SERVICES: 'Other Services',
  PAD_REPAIR: 'Pad Repair',
  PHASE_1: 'Phase I (ESA)',
  PHASE_2: 'Phase II (LSI)',
  PORTFOLIO_MONITORING: 'Portfolio Monitoring',
  PROPERTY_CONDITION_ASSESSMENT: 'Property Condition Assessment',
  PROPERTY_REPORT: 'Property Report',
  PCA_PRESCREEN: 'Property Condition PreScreen',
  CRIME_PRESCREEN: 'Crime PreScreen',
  REPORT_REVIEW: 'Report Review',
  REVISIONS: 'Revisions',
  TANK_INTEGRITY_INSPECTION: 'Tank Integrity Inspection',
  TANK_PULL: 'Tank Pull',
  WELL_DEVELOPMENT: 'Well Development',

  PROPERTY_CONDITION_ASSESSMENT_REVIEW: 'Property Condition Assessment Review',

  ENVIRONMENTAL_TRANSACTION_SCREEN_WITH_ENHANCED_FILE_REVIEW: 'Transaction Screen Assessment w/EFR',
  ENVIROSCREEN_WITH_ENHANCED_FILE_REVIEW: 'RSRA w/EFR',
  RSRA_SBA_WITH_ENHANCED_FILE_REVIEW: 'RSRA-SBA w/EFR',

  NEXUS_ZONING_COMPLIANCE_REPORT: 'Zoning Compliance Report',
  NEXUS_EZ_ZONING_WITH_COMPLIANCE: 'EZ Zoning Report with Compliance',
  NEXUS_EZ_ZONING_WITHOUT_COMPLIANCE: 'EZ Zoning Report without Compliance',
  NEXUS_USE_ANALYSIS_ZONING_REPORT: 'Zoning Use Analysis Report',
  ZONING_DOCUMENT_REPORT: 'Zoning Municipal Document Report',
  NEXUS_ALTA_SURVEY: 'ALTA/NSPS Land Title Survey',
  BOUNDARY_SURVEY: 'Boundary Survey',

  EARLY_INSIGHTS: 'Early Insights',
  CUSTOM_EARLY_INSIGHTS: 'Custom Early Insights',

  FANNIE_MAE_ZONING_REPORT: 'Fannie Mae Zoning Report',
  FREDDIE_MAC_ZONING_REPORT: 'Freddie Mac Zoning Report',

  FANNIE_MAE_PHASE_1: 'Fannie Mae Phase 1 (ESA)',
  FANNIE_MAE_PROPERTY_CONDITION_ASSESSMENT: 'Fannie Mae PCA',
  FANNIE_MAE_ALTA_SURVEY: 'Fannie Mae ALTA/NSPS Land Title Survey',
  FREDDIE_MAC_PHASE_1: 'Freddie Mac Phase 1 (ESA)',
  FREDDIE_MAC_PROPERTY_CONDITION_ASSESSMENT: 'Freddie Mac PCA',
  FREDDIE_MAC_ALTA_SURVEY: 'Freddie Mac ALTA/NSPS Land Title Survey',

  PCI_EXTERIOR_COMMERCIAL: 'Clear Capital - Property Condition Inspection, Drive-By',
  PCI_INTERIOR_COMMERCIAL: 'Clear Capital - Property Condition Inspection, Interior',
  PCI_EXTERIOR_RESIDENTIAL: 'Clear Capital - Residential Property Condition Inspection, Drive-By',
  PCI_INTERIOR_RESIDENTIAL: 'Clear Capital - Residential Property Condition Inspection, Interior',

  CONSTRUCTION_BUDGET_REVIEW: 'Construction Budget Review',
  PLAN_SPEC_COST_REVIEW: 'Plan, Specification & Cost Review',
  CONSTRUCTION_DRAW_MONITORING: 'Construction Draw Monitoring',

  LEGAL_DESCRIPTION: 'Legal Description',
  MAPPING_ASSISTANCE: 'Mapping Assistance',
  AREA_REPORT: 'Area Environmental Report',
  GROUND_LEASE_AND_FEE_REPORT: 'Ground Lease & Fee Report',
  GREENSTREET_AVM: 'Green Street AVM Pro',

  DATABASE_REVIEW_AUDIT: 'Database Review Audit',
  PROPERTY_CONDITION_ASSESSMENT_CORRECTION_REVIEW: 'Property Condition Assessment Correction Review',

  PORTFOLIO_ORDER: 'Portfolio Order',
  APPRAISAL_TECHNICAL_REVIEW: 'Commercial Appraisal - Technical Review',
  APPRAISAL_QC_REVIEW: 'Commercial Appraisal - Commercial Review',
  WATT_CARBON_ENERGY_CARBON_FOOTPRINT: 'Carbon Footprint PreScreen',

  OPERATIONS_MAINTENANCE_ASBESTOS: 'Operations & Maintenance - Asbestos',
  OPERATIONS_MAINTENANCE_LEAD_BASED_PAINT: 'Operations & Maintenance - Lead Based Paint',
  OPERATIONS_MAINTENANCE_MOISTURE: 'Operations & Maintenance - Moisture',

  SEISMIC_RISK_ASSESSMENT_PML: 'Seismic Risk Assessment/PML',
};
